import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useTranslator } from '@/hooks/useTranslator';
import {
  type NotificationStream,
  useUnsubscribePage,
} from '../-hooks/useUnsubscribePage';

const searchSchema = z.object({
  /**
   * The sha256 of the recipient's address, named `email` for the legacy
   * contract the notification emails already send.
   */
  email: z.string().optional(),
});

export const Route = createFileRoute('/unsubscribe/(pages)/')({
  validateSearch: searchSchema,
  component: UnsubscribePage,
});

const STREAMS: NotificationStream[] = ['realtime', 'daily', 'weekly'];

/**
 * Notification settings, reachable without signing in.
 *
 * Someone following an unsubscribe link has, by definition, not logged in, so
 * this route is exempt from the root auth guard (see `__root.tsx`) and
 * authenticates on the email hash instead.
 */
function UnsubscribePage() {
  const { email } = Route.useSearch();
  const { getTranslation: t } = useTranslator({ page: 'unsubscribe' });
  const page = useUnsubscribePage(email);

  return (
    <div className='min-h-dvh bg-muted flex items-start justify-center px-4 py-10 sm:py-16'>
      <Card className='w-full max-w-2xl'>
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
          <CardDescription>{t('subtitle')}</CardDescription>
        </CardHeader>

        <CardContent className='flex flex-col gap-4'>
          {page.isLoading ? (
            <>
              <Skeleton className='h-14 w-full' />
              <Skeleton className='h-14 w-full' />
              <Skeleton className='h-14 w-full' />
            </>
          ) : page.isUnknown ? (
            // No hash, or one the server does not recognise. Deliberately the
            // same message for both: telling an anonymous visitor which
            // hashes exist would answer a question they should not be able
            // to ask.
            <EmptyState
              title={t('notFound.title')}
              description={t('notFound.description')}
            />
          ) : (
            <>
              {STREAMS.map(stream => (
                <div
                  key={stream}
                  className='flex items-start justify-between gap-6 rounded-lg border border-border p-4'
                >
                  <div className='flex flex-col gap-1 min-w-0'>
                    <span className='text-xs font-semibold text-foreground'>
                      {t(`streams.${stream}.title`)}
                    </span>
                    <p className='text-xs text-muted-foreground'>
                      {t(`streams.${stream}.description`)}
                    </p>
                  </div>
                  <Switch
                    checked={page.choices[stream]}
                    disabled={page.isSaving}
                    aria-label={t(`streams.${stream}.title`)}
                    onCheckedChange={checked => page.setChoice(stream, checked)}
                  />
                </div>
              ))}

              <div className='flex justify-end pt-1'>
                <Button
                  loading={page.isSaving}
                  onClick={() => void page.submit(t)}
                >
                  {t('save')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
